import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { log } from '~/helpers';
import {
  Contact,
  ContactList,
  ContactOrGroup,
  ContactGroupList,
  ContactOrGroupList,
  ContactGroup,
  SmsTemplate,
  SmsSender,
} from '~/types/accountInfo';

export const RecipientValidationErrors = {
  INVALID_PHONE_NUMBER: 'INVALID_PHONE_NUMBER',
  TEN_DLC_TEMPLATE_REQUIRED: '10DLC_TEMPLATE_REQUIRED',
  COMREG_APPROVED_SENDER_REQUIRED: 'COMREG_APPROVED_SENDER_REQUIRED',
  EMPTY_CONTACT_GROUP: 'EMPTY_CONTACT_GROUP',
};

export function isContact(item: ContactOrGroup): item is Contact {
  return 'mobile' in item && 'id' in item;
}

export function isContactGroup(item: ContactOrGroup): item is ContactGroup {
  return 'contacts' in item && 'groupId' in item;
}

export function isTollFree(number: string): boolean {
  if (!number || typeof number !== 'string' || number.trim() === '')
    return false;

  const prefixes = [
    '+1800',
    '+1888',
    '+1877',
    '+1866',
    '+1855',
    '+1844',
    '+1833',
  ];
  return prefixes.some(prefix => number.startsWith(prefix));
}

// Normalize the number to ensure it always starts with '00' to keep same phonebook standard
export function parsePhonebookNumberStandard(number: string) {
  // If it starts with '00', keep as is; otherwise, add '00'
  if (number.startsWith('+')) return `00${number.slice(1)}`;
  return number;
}

/**
 * Try to parse/validate a phone number:
 * 1) As provided
 * 2) If invalid, try using the user's country (ISO like 'US')
 * 3) If still invalid, check if it starts with known country code and prepend '+'
 * 4) If still invalid, return the original input
 *
 * @param {string} inputNumber - the phone number the user provided
 * @param {string} userCountry - either 2-letter ISO (e.g. 'IE')
 * @returns {string} - E.164 formatted number if parsed & valid, otherwise original input
 */
export function normalizeE164OrReturnOriginal(
  inputNumber: string,
  userCountry: string
) {
  if (!inputNumber || typeof inputNumber !== 'string') return inputNumber;

  if (/[^0-9()+#\-\s]/.test(inputNumber)) return inputNumber;

  const knownCountryCodes = ['1', '44', '353', '55', '91', '380']; // US, UK, Ireland, Brazil, India, Ukraine

  // Normalize: replace leading 00 with + and remove all non-numeric characters
  let normalizedInput = inputNumber.trim().replace(/\D/g, '');
  if (normalizedInput.startsWith('00')) {
    normalizedInput = `+${normalizedInput.slice(2)}`;
  }

  // Helper to safely format a parsed phone number to E.164 (phone.number) or null
  const tryParse = (numStr: string, defaultCountry?: string) => {
    try {
      const phone = defaultCountry
        ? // @ts-ignore
          parsePhoneNumberFromString(numStr, defaultCountry)
        : parsePhoneNumberFromString(numStr);
      return phone && phone.isValid() ? phone.number /* E.164 */ : null;
    } catch (e) {
      return null;
    }
  };

  // 1) Try as-provided (with 00 -> + handled above)
  const asProvided = tryParse(normalizedInput);
  if (asProvided) return asProvided;

  // 2) If userCountry looks like an ISO (2 letters), try with it as defaultCountry
  if (typeof userCountry === 'string') {
    const withIso = tryParse(normalizedInput, userCountry.toUpperCase());
    if (withIso) return withIso;
  }

  //  3) Check if number starts with any known code and retry with '+'
  const hasInputStartsCountryCode = knownCountryCodes.some(code =>
    normalizedInput.startsWith(code)
  );
  if (hasInputStartsCountryCode) {
    const result = tryParse(`+${normalizedInput}`);
    if (result) return result;
  }

  // 4) Fallback: return the original input (could not validate/normalize)
  return inputNumber;
}

export function isValidPhoneNumberForSMS(number: string) {
  if (!number || typeof number !== 'string' || number.trim() === '')
    return false;

  // Replace 00 prefix with +
  const normalized = number.startsWith('00') ? `+${number.slice(2)}` : number;

  const phoneNumberInfo = parsePhoneNumberFromString(normalized);

  if (!phoneNumberInfo || !phoneNumberInfo.isValid()) {
    return false;
  }

  const type = phoneNumberInfo.getType();

  // Return true if it's mobile or toll free
  if (
    type === 'MOBILE' ||
    type === 'FIXED_LINE_OR_MOBILE' ||
    type === 'TOLL_FREE'
  ) {
    return true;
  }

  // Return true if it's landline from USA
  if (type === 'FIXED_LINE' && phoneNumberInfo.country === 'US') {
    return true;
  }

  return false;
}

export function getInvalidParticipants(
  participants: ContactOrGroupList,
  template: SmsTemplate | null | undefined,
  sender: SmsSender | null | undefined
) {
  if (!participants || participants.length === 0) {
    return [];
  }

  return participants
    .map((participant: ContactOrGroup) => {
      if (isContact(participant)) {
        const { mobile, landline } = participant;

        let phoneNumber = mobile || landline || '';

        if (phoneNumber.startsWith('00')) {
          phoneNumber = `+${phoneNumber.slice(2)}`;
        }

        // If NOT valid number for SMS at all
        if (!isValidPhoneNumberForSMS(phoneNumber)) {
          return {
            ...participant,
            metadata: {
              error: `The ${phoneNumber} is an invalid phone number.`,
              warning: null,
              info: {},
              errorCode: RecipientValidationErrors.INVALID_PHONE_NUMBER,
            },
          };
        }

        const phoneNumberInfo = parsePhoneNumberFromString(phoneNumber);

        // Toll free number should not enfore template
        if (phoneNumberInfo?.country === 'US') {
          // If it's toll-free, no need to check for 10DLC template
          if (
            sender?.name &&
            !/[a-zA-Z]/.test(sender?.name) &&
            isTollFree(sender.name)
          )
            return null;

          if (
            phoneNumberInfo.getType() !== 'TOLL_FREE' &&
            ((template?.usage && template?.usage !== '10dlc') || sender)
          ) {
            return {
              ...participant,
              metadata: {
                error: null,
                warning: `The ${phoneNumber} phone number requires 10DLC template.`,
                info: phoneNumberInfo,
                errorCode: RecipientValidationErrors.TEN_DLC_TEMPLATE_REQUIRED,
              },
            };
          }
        }

        // ComReg sender validation for Irish numbers
        if (
          phoneNumberInfo?.country === 'IE' &&
          sender &&
          !sender?.requestedForIreland
        ) {
          return {
            ...participant,
            metadata: {
              error: null,
              warning: `The ${phoneNumber} phone number requires approved sender ID.`,
              info: phoneNumberInfo,
              errorCode:
                RecipientValidationErrors.COMREG_APPROVED_SENDER_REQUIRED,
            },
          };
        }
      } else if (isContactGroup(participant)) {
        if (!participant.contacts || participant.contacts.length === 0) {
          return {
            ...participant,
            metadata: {
              error: `${participant.groupName} has no contacts. Head to the phonebook to add some or take it out to send your message.`,
              warning: null,
              info: {},
              errorCode: RecipientValidationErrors.EMPTY_CONTACT_GROUP,
            },
          };
        }
      }
      return null;
    })
    .filter(Boolean);
}

/**
 * Removes contact groups from source that already exist in target by groupId
 */
export function filterSelectedGroups(
  source: ContactGroupList,
  target: ContactGroupList
): ContactGroupList {
  return source.filter(
    (group: ContactGroup) =>
      !target.some(
        (existing: ContactGroup) => existing.groupId === group.groupId
      )
  );
}

/**
 * Removes contact from source that already exist in target by ID
 */
export function filterSelectedContacts(
  source: ContactList,
  target: ContactList
): ContactList {
  return source.filter(
    (contact: Contact) =>
      !target.some((existing: Contact) => existing.id === contact.id)
  );
}

/**
 * Create a random contact ID for user-inserted numbers that don’t exist in the phonebook or groups.
 */
export function generateRandomUnlistedContactId() {
  const prefix = '900';
  const length = 15;
  const randomDigits = Math.floor(
    Math.random() * 10 ** (length - prefix.length)
  )
    .toString()
    .padStart(length - prefix.length, '0');
  return prefix + randomDigits;
}

export function filterOptions(
  options: ContactOrGroupList,
  state: { inputValue: string }
) {
  return options.filter((option: ContactOrGroup) => {
    if (isContact(option)) {
      return (
        option.mobile.toLowerCase().includes(state.inputValue.toLowerCase()) ||
        option.name.toLowerCase().includes(state.inputValue.toLowerCase())
      );
    }

    return option.name.toLowerCase().includes(state.inputValue.toLowerCase());
  });
}

/**
 * Make contact phone number more readable in the Chip label.
 * e.g, 00353851234567 => +353 85 123 4567 If
 */
export const formatContactNameOrNumberLabel = ({ name, mobile }: Contact) => {
  try {
    if (name) return name;

    if (mobile) {
      // Remove all non-numeric characters
      const mobileNumeric = mobile.replace(/\D/g, '');

      const mobileInternational = parsePhoneNumberFromString(
        mobileNumeric.startsWith('00')
          ? `+${mobileNumeric.slice(2)}`
          : `+${mobileNumeric}`
      )?.formatInternational();

      if (mobileInternational) return mobileInternational;
    }
  } catch (err) {
    log.error('Error formatting contact label.', err);
  }

  return mobile;
};

export function createRecipientContact({
  id,
  name,
  mobile,
}: {
  id?: number;
  name?: string | null;
  mobile: string;
}): Contact {
  return {
    id: id || Number(generateRandomUnlistedContactId()),
    name: name || '',
    mobile,
  };
}
